import { locale } from 'svelte-i18n';
import { get } from 'svelte/store';

const intlLocaleMap: Record<string, string> = {
    no: 'nb-NO',
    sv: 'sv-SE',
    da: 'da-DK',
    en: 'en-GB'
};

function resolveLocale() {
    const activeLocale = get(locale);
    return typeof activeLocale === 'string' && activeLocale.length > 0 ? activeLocale : 'no';
}

function resolveIntlLocale() {
    return intlLocaleMap[resolveLocale()] ?? intlLocaleMap.no;
}

// Formatting a table should reuse Intl instances instead of constructing one per
// cell. Include locale and every option; bound retention for custom formats.
const numberFormats = new Map<string, Intl.NumberFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();
function cachedFormatter<T>(cache: Map<string, T>, options: object, create: (locale: string) => T): T {
    const language = resolveIntlLocale();
    const key = JSON.stringify([language, options]);
    let formatter = cache.get(key);
    if (!formatter) {
        formatter = create(language);
        if (cache.size >= 64) cache.delete(cache.keys().next().value!);
        cache.set(key, formatter);
    }
    return formatter;
}

export function formatNumber(value: number, options: Intl.NumberFormatOptions = {}) {
    return cachedFormatter(numberFormats, options, language => new Intl.NumberFormat(language, options)).format(value);
}

export function formatNOK(value: number) {
    return formatNumber(value, {
        style: 'currency',
        currency: 'NOK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
}

export function formatDate(
    value: string | number | Date | null | undefined,
    options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
    }
) {
    if (!value) {
        return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return cachedFormatter(dateFormats, options, language => new Intl.DateTimeFormat(language, options)).format(date);
}

export function formatDateTime(
    value: string | number | Date | null | undefined,
    options: Intl.DateTimeFormatOptions = {
        dateStyle: 'short',
        timeStyle: 'medium'
    }
) {
    return formatDate(value, options);
}
